import React, { useState } from "react";
import Slider from "react-slick";
import Loading from "../Loading/Loading";
import { COLORS } from "../../constants";
import "../../assets/css/slick.css";

const styles = {
  container: (height) => ({
    height: `${height}px`,
    position: "relative",
    overflow: "hidden",
  }),
  slideWrapper: (height) => ({
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    height: `${height}px`,
    cursor: "pointer",
    backgroundColor: "black",
  }),
  image: (height) => ({
    maxHeight: `${height}px`,
    maxWidth: "100%",
    height: `${height}px`,
    objectFit: "contain",
    display: "block",
    margin: "0 auto",
    borderRadius: "8px",
  }),
  overlay: (height) => ({
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: `${height}px`,
    pointerEvents: "none",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2,
  }),
  arrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "55px",
    color: COLORS.border,
    backgroundColor: COLORS.textLight,
    width: "55px",
    height: "55px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 3,
  },
  prevArrow: {
    left: "10px",
  },
  nextArrow: {
    right: "10px",
  },
  arrowIcon: {
    display: "block",
    position: "relative",
    bottom: "5px",
    marginLeft: "2px",
    lineHeight: 1,
  },
  prevArrowIcon: {
    right: "1px",
  },
  nextArrowIcon: {
    left: "1px",
  },
};

const PrevArrow = ({ onClick }) => (
  <div
    style={{ ...styles.arrow, ...styles.prevArrow }}
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick?.();
      }
    }}
    aria-label="Previous slide"
  >
    <span style={{ ...styles.arrowIcon, ...styles.prevArrowIcon }}>&#8249;</span>
  </div>
);

const NextArrow = ({ onClick }) => (
  <div
    style={{ ...styles.arrow, ...styles.nextArrow }}
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick?.();
      }
    }}
    aria-label="Next slide"
  >
    <span style={{ ...styles.arrowIcon, ...styles.nextArrowIcon }}>&#8250;</span>
  </div>
);

const SlideWithOverlay = ({ src, idx, overlay, handleImageLoad, onPress, height }) => (
  <div
    style={styles.slideWrapper(height)}
    role="button"
    tabIndex={0}
    onClick={() => onPress?.(src, idx)}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onPress?.(src, idx);
      }
    }}
  >
    <img
      src={typeof src === "string" ? src : src?.default}
      alt={`Slide ${idx + 1}`}
      style={styles.image(height)}
      onLoad={() => {
        handleImageLoad();
      }}
      onError={(e) => {
        console.error(`Image ${idx} failed to load:`, src);
        handleImageLoad();
      }}
    />
    {overlay && <div style={styles.overlay(height)}>{overlay(idx)}</div>}
  </div>
);

const ImageCarousel = ({ data, height, overlay, onPress }) => {
  const [loadedCount, setLoadedCount] = useState(0);
  const totalImages = data?.images?.length || 0;
  const allLoaded = totalImages > 0 && loadedCount >= totalImages;

  const handleImageLoad = () => {
    setLoadedCount((prev) => prev + 1);
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
  };

  return (
    <div style={styles.container(height)} role="region" aria-label="Image carousel">
      {!allLoaded && <Loading />}
      <Slider {...settings}>
        {data.images.map((src, idx) => (
          <SlideWithOverlay
            key={idx}
            src={src}
            idx={idx}
            overlay={overlay}
            handleImageLoad={handleImageLoad}
            onPress={onPress}
            height={height}
          />
        ))}
      </Slider>
    </div>
  );
};

export default ImageCarousel;
